import { ActionSheetButton, IonRippleEffect, IonSpinner, IonText, useIonActionSheet, useIonAlert } from '@ionic/react';
import { pause, play, square } from 'ionicons/icons';
import React, { useEffect, useState } from 'react';

import { useHistory } from 'react-router';
import { slowTimer } from '../../common/interval';
import { logTransaction } from '../../common/log';
import { getLocationOdomoeter } from '../../common/mapsUtil';
import { LogType } from '../../models/log.model';
import { RouteNames } from '../../models/routes.model';
import { useLocation } from '../../providers/LocationProvider';
import store from '../../store';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { resetDistanceTrackingDataStoreAsync, setAddressState } from '../../store/locationSlice';
import { setDistanceTrackingUserActivatedStoreAsync, setDistanceTrackingUserPausedStoreAsync } from '../../store/settingsSlice';
import { IonCardWrapper } from './DistanceTrackingCard.styles';

interface IComponentProps {
  type?: DistanceTrackingCardType;
}

export enum DistanceTrackingCardType {
  full,
  simple,
}

const DistanceTrackingCard: React.FC<IComponentProps> = ({ type = DistanceTrackingCardType.full }) => {
  const [odo, setOdo] = useState(0);
  const [presentAlert] = useIonAlert();
  const [presentActionSheet] = useIonActionSheet();
  const {
    actions: { getGeocodedAddressData },
  } = useLocation();

  const { isDebug, isDemo } = useAppSelector((state) => state.app);
  const { gpsServicesAvailable, distanceTrackingUserPaused, isDarkMode, gpsWatchStarting } = useAppSelector((state) => state.settings);
  const { address } = useAppSelector((state) => state.location);

  const history = useHistory();
  const dispatch = useAppDispatch();

  useEffect(() => {
    setCurrentAddress();

    slowTimer.subject.subscribe(async (id: number) => {
      if (id >= 0) {
        if (isDemo) {
          setOdo(1566);
        } else {
          const odo = await getLocationOdomoeter();
          setOdo(odo);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClickTrackingCard = () => {
    const { distanceTrackingUserActivated, gpsServicesAvailable, distanceTrackingUserPaused } = store.getState().settings;

    if (distanceTrackingUserActivated && gpsServicesAvailable) {
      let actionSheetButtons = [
        {
          text: distanceTrackingUserPaused ? 'Resume Tracking' : 'Pause Tracking',
          icon: distanceTrackingUserPaused ? play : pause,
          handler: async () => {
            if (distanceTrackingUserPaused) {
              // Resume
              logTransaction(LogType.Info, `Resume location tracking requested`);
              await dispatch(setDistanceTrackingUserPausedStoreAsync(false));
            } else {
              // Pause
              logTransaction(LogType.Info, `Pause location tracking requested`);
              await dispatch(setDistanceTrackingUserPausedStoreAsync(true));
            }
          },
        },
        {
          text: 'Cancel Tracking',
          icon: square,
          handler: async () => {
            await presentAlert({
              header: 'Cancel Tracking?',
              subHeader: '',
              message: 'Would you like to cancel the current tracking and clear all distance data?',
              buttons: [
                {
                  text: 'No',
                  role: 'cancel',
                  cssClass: 'secondary-button',
                },
                {
                  text: 'Yes',
                  cssClass: 'secondary-button',
                  handler: () => {
                    logTransaction(LogType.Info, `Cancel location tracking requested`);
                    disableLocationServices();
                  },
                },
              ],
            });
          },
        },
      ] as ActionSheetButton[];

      if (process.env.REACT_APP_PRODUCTION === 'false' || process.env.REACT_APP_DEBUG === 'true' || isDebug) {
        // actionSheetButtons.push(
        //   {
        //     text: 'Fake Movement',
        //     icon: location,
        //     handler: async () => {
        //       const locations = await getLocations();
        //       const randomLocation = getFakeLocation();
        //       if (locations.length === 0) {
        //         handleLocation({
        //           ...randomLocation,
        //           time: new Date().getTime(),
        //         } as IGeolocation);
        //       } else {
        //         if (locations.length) {
        //           const lastLocation = locations[locations.length - 1];
        //           const newLocation = moveTowards(lastLocation.latitude, lastLocation.longitude, randomLocation.latitude, randomLocation.longitude, Math.floor(Math.random() * 50.0));
        //           const randomN = Math.floor(Math.random() * 50) + 10;
        //           const randomE = Math.floor(Math.random() * 50) + 10;
        //           const newLocation = moveDistance(lastLocation.latitude, lastLocation.longitude, randomN, randomE);
        //           handleLocation({
        //             latitude: newLocation.latitude,
        //             longitude: newLocation.longitude,
        //             accuracy: 5,
        //             time: new Date().getTime(),
        //           } as IGeolocation, true);
        //         }
        //       }
        //     }
        //   });
      }

      presentActionSheet({
        header: 'Options',
        buttons: actionSheetButtons,
      });
    } else {
      if (!gpsServicesAvailable) {
        history.push({
          pathname: RouteNames.settings,
        });
      }
    }
  };

  const getDistanceRounded = (distance: number) => {
    if (distance >= 1000) {
      return (distance / 1000).toFixed(2);
    } else {
      return distance.toFixed(0);
    }
  };

  const getDistanceUnit = (distance: number) => {
    if (distance >= 1000) {
      return 'km';
    } else {
      return 'm';
    }
  };

  const setCurrentAddress = async () => {
    const currentLocation = store.getState().location.lastPosition;
    if (currentLocation) {
      const address = await getGeocodedAddressData(currentLocation);
      await dispatch(setAddressState(address));
    }
    // Demo
    if (isDemo) {
      await dispatch(
        setAddressState({
          city: 'Melbourne',
          streetName: '36 Church St',
        }),
      );
    }
  };

  const disableLocationServices = async () => {
    logTransaction(LogType.Info, `Disabling location tracking`);
    await dispatch(setDistanceTrackingUserActivatedStoreAsync(false));
    await dispatch(resetDistanceTrackingDataStoreAsync());
  };

  return (
    <IonCardWrapper className={`clocking-card ion-activatable ripple-card flexed col tracking-card ${type === DistanceTrackingCardType.simple ? 'simple' : ''}`}>
      <IonRippleEffect type="bounded" />
      {gpsServicesAvailable && (
        <div className="flexed col" onClick={() => handleClickTrackingCard()}>
          {!isDarkMode && distanceTrackingUserPaused && (
            <div className="disabled-mask">
              <IonText color="white">
                <h3 className="ion-text-center">Location services paused</h3>
              </IonText>
            </div>
          )}
          {isDarkMode && distanceTrackingUserPaused && (
            <div className="disabled-mask dark">
              <IonText color="white">
                <h3 className="ion-text-center">Location services paused</h3>
              </IonText>
            </div>
          )}
          <div className="flexed col">
            <div className="location-display">
              <div className="address-container ion-margin">
                <p className="route">{address?.city}</p>
              </div>
              <div className="distance-container ion-margin">
                {!isDarkMode && (
                  <div className="distance">
                    {getDistanceRounded(odo)}
                    <div className="unit">{getDistanceUnit(odo)}</div>
                  </div>
                )}
                {isDarkMode && (
                  <div className="distance dark">
                    {getDistanceRounded(odo)}
                    <div className="unit">{getDistanceUnit(odo)}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {!gpsServicesAvailable && (
        <div className="flexed col flexed-center unavailable-display" onClick={() => handleClickTrackingCard()}>
          {gpsWatchStarting && (
            <div className="spinner">
              <IonSpinner name="lines-small" className="text-spinner" />
            </div>
          )}
          <IonText color="white">
            <h3 className="ion-text-center">Location services unavailable</h3>
          </IonText>
        </div>
      )}
    </IonCardWrapper>
  );
};

export default DistanceTrackingCard;
